<template>
  <NuxtLink
    v-if="resource"
    class="group flex cursor-pointer gap-4 hover:bg-primary-alpha-50 md:gap-6"
    :to="goToResource(resource)"
    :external="resource.type?.toLowerCase() === 'article'"
  >
    <BasicIcon
      v-if="!hideIcon && !minified"
      :type="resource.type"
      class="h-8 w-8 shrink-0 transition-colors duration-200 ease-in-out group-hover:text-primary-400 md:h-10 md:w-10 lg:h-16 lg:w-16"
    />
    <div :class="minified ? 'flex items-baseline gap-2' : 'flex-auto'">
      <div class="flex items-center justify-between gap-4">
        <h4
          v-if="locale === 'fr'"
          :class="
            minified
              ? 'my-0 font-semibold transition-colors duration-200 ease-in-out group-hover:text-primary-400 md:text-base'
              : 'font-semibold transition-colors duration-200 ease-in-out group-hover:text-primary-400 md:text-base'
          "
        >
          <span v-if="!resource.titleHtml && resource.title">
            {{ removeDuration(resource.title) }}
          </span>
          <span v-else-if="resource.titleHtml">
            <span v-html="resource.titleHtml" />
          </span>
        </h4>
        <h4
          v-else-if="!resource.titleEn && resource.titleHtml"
          class="font-semibold transition-colors duration-200 ease-in-out group-hover:text-primary-400 md:text-base"
        >
          <span v-html="resource.titleHtml" />
        </h4>
        <h4
          v-else
          :class="
            minified
              ? 'font-semibold transition-colors duration-200 ease-in-out group-hover:text-primary-400 md:text-base my-0'
              : 'font-semibold transition-colors duration-200 ease-in-out group-hover:text-primary-400 md:text-base'
          "
        >
          {{
            removeDuration(resource.titleEn) ?? removeDuration(resource.title)
          }}
        </h4>
        <div v-if="!minified" class="flex shrink-0 items-center gap-4">
          <DataPill v-if="resource.type" class="hidden md:flex">
            <span
              v-if="te(`resource.type.partition`)"
              class="first-letter:uppercase text-base"
            >
              {{ $t(`resource.type.${resource.type}`) }}
            </span>
            <span v-else class="first-letter:uppercase text-base">{{
              resource.type
            }}</span>
          </DataPill>

          <ChevronRight class="h-4 w-4 shrink-0" />
        </div>
      </div>
      <p :class="minified ? 'text-base md:my-1' : 'mt-1 md:mt-0 text-justify'">
        <span
          v-if="resource.subtitle || resource.reference"
          v-html="shortenSubtitle(resource.subtitle || resource.reference)"
        />

        <span v-if="resource.year" class="text-primary-300 ml-4">{{
          resource.year
        }}</span>
        <span v-if="resource.documentationDate || resource.performanceDate">
          (<span v-if="resource.performanceDate"
            >Performance date: {{ resource.performanceDate }}</span
          ><span v-if="resource.performanceDate && resource.documentationDate"
            >,</span
          >
          <span v-if="resource.documentationDate">
            Documentation date: {{ resource.documentationDate }}</span
          >)
        </span>

        <span v-if="resource.available" class="text-status-valid-400">
          {{ $t('resource.available') }}
        </span>
        <span
          v-else-if="resource.available !== undefined && !resource.available"
          class="text-status-invalid-400"
        >
          {{ $t('resource.not_available') }}
        </span>
      </p>
      <p
        v-if="resource.excerpt && locale === 'fr'"
        class="text-shade-500"
        v-html="resource.excerpt"
      />
      <p
        v-else-if="resource.excerpt && locale === 'en' && !resource.excerptEn"
        class="text-shade-500"
        v-html="resource.excerpt"
      />
      <p
        v-else-if="resource.excerptEn"
        class="text-shade-500"
        v-html="resource.excerptEn"
      />

      <DataPill
        v-if="
          !['composer', 'author', 'composer-video', 'composer-audio'].includes(
            resource.type
          )
        "
        class="mt-4 w-fit md:hidden"
      >
        <span
          v-if="te(`resource.type.${resource.type}`)"
          class="first-letter:uppercase"
        >
          {{ $t(`resource.type.${resource.type}`) }}
        </span>
        <span v-else class="first-letter:uppercase">{{ resource.type }}</span>
      </DataPill>
      <div class="flex">
        <DataPill
          v-for="tag in resource.tags"
          :key="tag"
          class="ml-2 mt-4 w-fit text-sm"
          is-gray
          :highlight="tag.label ?? tag === 'valid'"
        >
          {{ tag.label ? tag.label : tag }}
        </DataPill>
        <div v-if="resource.tags?.length === 0" class="ml-2 mt-4 w-fit text-sm">
          &nbsp;
        </div>
      </div>
    </div>
  </NuxtLink>
</template>
<script lang="ts" setup>
import { PropType } from 'vue'
import { useI18n } from '#i18n'
import ChevronRight from '~/assets/icon/navigation/Chevron/Right.svg'

const { te, locale } = useI18n()

function shortenSubtitle(value) {
  let extract = value.slice(0, 240)
  if (value.includes('<mark>') && !extract.includes('<mark>')) {
    const values = value.slice(240).split(' ')
    let lastCloseIndex
    let firstOpenIndex
    for (let i = 0; i < values.length; i++) {
      if (lastCloseIndex && lastCloseIndex + 4 === i) {
        break
      }
      if (!firstOpenIndex && values[i].includes('<mark>')) {
        firstOpenIndex = i
      }
      if (values[i].includes('</mark>')) {
        lastCloseIndex = i
      }
    }
    if (firstOpenIndex && !lastCloseIndex) {
      lastCloseIndex = values.length
    }
    if (firstOpenIndex && lastCloseIndex) {
      extract += `... ${values
        .slice(firstOpenIndex - 4, lastCloseIndex + 4)
        .join(' ')}...`
    }
  }
  extract = extract.replaceAll('<mark>', '').replaceAll('</mark>', '')
  return extract
}
defineProps({
  resource: {
    type: Object as PropType<ResourceList>,
    required: true,
  },
  hideIcon: {
    type: Boolean,
    default: false,
  },
  minified: {
    type: Boolean,
    default: false,
  },
})

function removeDuration(value) {
  if (!value) return
  return value.replace(/[0-9]+ h/, '').replace(/[0-9]+ min/, '')
}
const localePath = useLocalePath()
const goToResource = (resource: ResourceList | ResourceCard) => {
  switch (resource.type?.toLowerCase()) {
    case 'collection':
      return localePath(`/collection/${resource.slug}/`)
    case 'article':
      return resource.slug
    case 'partition':
    case 'program':
    case 'monographie':
    case 'cd':
    case 'n-periodique':
    case 'thesis':
    case 'livre':
    case 'congres':
    case 'film':
    case 'catalog':
    case 'cd-rom':
    case 'documentary':
    case 'periodique':
    case 'press':
    case 'vinyl':
    case 'flora-article':
    case 'book':
      return localePath(`/flora/${resource.slug}/`)
    case 'author':
      return localePath(`/author/${resource.slug}/articles`)
    case 'biography':
      return localePath(
        `/composer/${resource.slug.replace('biography-', '')}/biography`
      )
    case 'composer':
      if (resource.page === 'advanced_search') {
        return localePath(`/composer/${resource.slug}/biography`)
      } else {
        return localePath(`/search_result?search=${resource.title}`)
      }
    case 'composer-audio':
      return localePath(`/composer/${resource.slug}/audios`)
    case 'composer-video':
      return localePath(`/composer/${resource.slug}/videos`)
    case 'workcourse':
      return localePath(
        `/composer/${resource.slug.replace('workcourse-', '')}/workcourse`
      )
    case 'worklist':
      return `/composer/${resource.slug}/worksByDate`
    case 'analysis':
      return localePath(`/analysis/${resource.slug}/`)
    case 'video':
    case 'audio':
      return localePath(`/media/${resource.slug}/`)
    case 'event':
      return localePath(`/event/${resource.slug}/`)
    case 'work':
      return localePath(`/work/${resource.slug}/`)
    case 'sidney':
      return localePath(`/sidney/${resource.slug}/`)
  }
}
</script>
